import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {FilterDropdownBox} from '../../../../components/common/filters/filterDropdownBox/filterDropdownBox';
import {MoreFiltersButton} from '../../../../components/common/filters/moreFiltersDropdown/moreFiltersButton';
import {computeIsGlobalNamespace} from '../../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {InterfaceEventsEnum} from '../../../../services/tracking';
import {getAnalyticsFilters} from '../../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {getAnalyticsSelectedNamespace} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {AnalyticsSelectedViewId} from '../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {useAnalyticsInboxesFilter} from '../../filters/useAnalyticsInboxesFilter';
import {useAnalyticsTeammatesFilter} from '../../filters/useAnalyticsTeammatesFilter';
import {useAnalyticsTeamsFilter} from '../../filters/useAnalyticsTeamsFilter';
import {ReportHeaderDatepicker} from '../titleRow/reportHeaderDatepicker';
import {ReportHeaderSaveViewDropdown} from './dialogs/reportHeaderSaveViewDropdown';
import {ReportHeaderFiltersMoreButton} from './reportHeaderFiltersMoreButton';
import {ReportHeaderFiltersResetButton} from './reportHeaderFiltersResetButton';
import {useExtraFilters} from './useExtraFilters';

/*
 * Props.
 */

interface ReportHeaderFiltersProps {
  selectedViewId: AnalyticsSelectedViewId;
}

/*
 * Styles.
 */

const StyledHeaderFiltersDiv = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 16px;

  justify-content: space-between;
`;

const StyledFilterBarSection = css`
  display: flex;

  > *:not(:last-child) {
    margin-right: 8px;
  }
`;

const StyledFiltersWrapperDiv = styled.div`
  ${StyledFilterBarSection};
  flex-wrap: wrap;
`;

const StyledRightButtonsWrapperDiv = styled.div`
  ${StyledFilterBarSection};
  margin-top: 8px;
`;

const StyledDateSelectorWrapperDiv = styled.div`
  margin-top: 8px;
`;

const StyledFilterBarItemWrapperDiv = styled.div`
  max-width: 300px;
  margin-top: 8px;
`;

const StyledButtonWrapperDiv = styled.div`
  ${flexCenter};
  margin-top: 8px;
`;

/*
 * Component.
 */

export const ReportHeaderFilters: FC<ReportHeaderFiltersProps> = ({selectedViewId}) => {
  const selectedNamespace = useAppSelector(getAnalyticsSelectedNamespace);
  const filters = useAppSelector(getAnalyticsFilters);
  const trackAnalytics = useAnalyticsTracking();

  /*
   * Filter Hooks.
   */

  const {
    isActive: isTeammatesFilterActive,
    renderDropdown: teammatesFilterDropdownRenderer,
    renderButton: teammatesFilterButtonRenderer,
  } = useAnalyticsTeammatesFilter();
  const {
    isActive: isTeamsFilterActive,
    renderDropdown: teamsFilterDropdownRenderer,
    renderButton: teamsFilterButtonRenderer,
  } = useAnalyticsTeamsFilter();
  const {
    isActive: isInboxesFilterActive,
    renderDropdown: inboxesFilterDropdownRenderer,
    renderButton: inboxesFilterButtonRenderer,
  } = useAnalyticsInboxesFilter();

  const {activeExtraFilters, inactiveExtraFilters} = useExtraFilters();

  /*
   * Tracking.
   */

  // Tracks when the "More Filters" button is clicked to open the dropdown.
  const onMoreFiltersOpen = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_ADD_FILTER_CLICK);
  };

  // Tracks whenever an in-active filter is selected to open the dropdown
  // for editing (whether that inactive filter is in the filter bar or in the
  // more filters dropdown).
  const onFilterSelect = (type: AnalyticsFilterTypesEnum) => {
    const isApplied = Boolean(filters[type]);
    if (!isApplied) {
      trackAnalytics(InterfaceEventsEnum.ANALYTICS_FILTER_SELECT, {
        filter_type: type,
      });
    }
  };

  /*
   * Helpers.
   */

  const renderStickyFilters = () => (
    <>
      <StyledDateSelectorWrapperDiv>
        <ReportHeaderDatepicker />
      </StyledDateSelectorWrapperDiv>
      {/* The teams filter is only available in the global namespace */}
      {computeIsGlobalNamespace(selectedNamespace) && (
        <StyledFilterBarItemWrapperDiv>
          <FilterDropdownBox
            isActive={isTeamsFilterActive}
            renderButton={teamsFilterButtonRenderer}
            renderDropdown={teamsFilterDropdownRenderer}
          />
        </StyledFilterBarItemWrapperDiv>
      )}
      <StyledFilterBarItemWrapperDiv>
        <FilterDropdownBox
          isActive={isInboxesFilterActive}
          renderButton={inboxesFilterButtonRenderer}
          renderDropdown={inboxesFilterDropdownRenderer}
        />
      </StyledFilterBarItemWrapperDiv>
      <StyledFilterBarItemWrapperDiv>
        <FilterDropdownBox
          isActive={isTeammatesFilterActive}
          renderButton={teammatesFilterButtonRenderer}
          renderDropdown={teammatesFilterDropdownRenderer}
        />
      </StyledFilterBarItemWrapperDiv>
    </>
  );

  const maybeRenderActiveFilters = () => (
    <>
      {activeExtraFilters.map(({id, isActive, renderDropdown, renderButton}) => (
        <StyledFilterBarItemWrapperDiv key={id}>
          <FilterDropdownBox
            key={id}
            isActive={isActive}
            renderButton={renderButton}
            renderDropdown={renderDropdown}
          />
        </StyledFilterBarItemWrapperDiv>
      ))}
    </>
  );
  const maybeRenderMoreFilters = () => {
    if (inactiveExtraFilters.length === 0) {
      return null;
    }

    return (
      <MoreFiltersButton
        dropdownItems={inactiveExtraFilters.map(({title, renderDropdown, id, type}) => ({
          renderDropdown,
          title,
          id,
          onItemSelect: () => onFilterSelect(type),
        }))}
        onOpen={onMoreFiltersOpen}
      />
    );
  };

  /*
   * Render.
   */

  return (
    <StyledHeaderFiltersDiv>
      <StyledFiltersWrapperDiv>
        {renderStickyFilters()}
        {maybeRenderActiveFilters()}
        {/* Put the buttons in an item wrapper together so that they wrap lines as a group.
        This way we avoid having an orphaned item on the second line */}
        <StyledButtonWrapperDiv>
          {maybeRenderMoreFilters()}
          <ReportHeaderFiltersResetButton />
        </StyledButtonWrapperDiv>
      </StyledFiltersWrapperDiv>
      <StyledRightButtonsWrapperDiv>
        <ReportHeaderSaveViewDropdown />
        <ReportHeaderFiltersMoreButton selectedViewId={selectedViewId} />
      </StyledRightButtonsWrapperDiv>
    </StyledHeaderFiltersDiv>
  );
};
